import gsap from 'gsap';
import { useLayoutEffect, type RefObject } from 'react';

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Small, consistent motion: content rises in on mount, children stagger.
 * Everything is skipped for people who asked their OS for reduced motion.
 */
export function useRiseIn<T extends HTMLElement>(ref: RefObject<T>, deps: unknown[] = [], selector?: string): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || reduced()) return;
    const targets = selector ? el.querySelectorAll(selector) : [el];
    const ctx = gsap.context(() => {
      gsap.fromTo(targets, { y: 8, opacity: 0 }, { y: 0, opacity: 1, duration: 0.42, ease: 'power3.out', stagger: 0.045, clearProps: 'transform,opacity' });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Counts a number up from 0 the first time it renders (stat tiles). */
export function animateNumber(el: HTMLElement | null, to: number, format: (n: number) => string): void {
  if (!el) return;
  if (reduced()) {
    el.textContent = format(to);
    return;
  }
  const state = { v: 0 };
  gsap.to(state, { v: to, duration: 0.9, ease: 'power2.out', onUpdate: () => (el.textContent = format(state.v)) });
}

export { gsap, reduced };
